import React, { useState, useRef, useCallback } from 'react';
import { ChevronsLeftRight, Sparkles, TrendingUp } from 'lucide-react';
import PrinterStrip from './PrinterStrip';

const IMAGE = 'https://images.unsplash.com/photo-1693031630369-bd429a57f115?w=1400&q=85';
const BAD_CHIPS = ['#8a999a', '#a66a80', '#b8a860'];
const GOOD_CHIPS = ['#00B7C7', '#E5007E', '#FFD700'];

const CalibrationHeader = () => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
    <div className="max-w-2xl">
      <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3 inline-flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> Calibration in action
      </p>
      <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight leading-tight">
        Before &amp;{' '}
        <span className="pn-gradient-text">
          after
        </span>{' '}
        calibration
      </h2>
      <p className="mt-4 text-slate-600">
        Drag the slider left ↔ right to see the difference between an uncalibrated print and one
        measured with a Barbieri Spectro qb.
      </p>
    </div>

    {/* Metrics */}
    <div className="grid grid-cols-2 gap-3">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 min-w-[140px]">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">ΔE average</p>
        <p className="text-2xl font-black font-mono text-[#050F2E]">6.8 <span className="text-slate-400">→</span> <span className="text-[#0F7B3F]">0.7</span></p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 min-w-[140px]">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Saturation</p>
        <p className="text-2xl font-black font-mono text-[#050F2E]">-38% <span className="text-slate-400">→</span> <span className="text-[#0F7B3F]">100%</span></p>
      </div>
    </div>
  </div>
);

const CompareSlider = ({ containerRef, pos, onDown, onTouchStart, onTouchMove, onTouchEnd }) => (
  <div
    ref={containerRef}
    className="relative aspect-[16/9] rounded-3xl overflow-hidden shadow-2xl border border-slate-100 select-none cursor-ew-resize"
    onMouseDown={onDown}
    onTouchStart={onTouchStart}
    onTouchMove={onTouchMove}
    onTouchEnd={onTouchEnd}
  >
    {/* Calibrated (right side, base) */}
    <img src={IMAGE} alt="Calibrated print" className="absolute inset-0 w-full h-full object-cover" draggable={false} />

    {/* Uncalibrated overlay (left side, clipped by pos) */}
    <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
      <img
        src={IMAGE}
        alt="Uncalibrated print"
        className="w-full h-full object-cover"
        style={{ filter: 'saturate(0.35) contrast(0.82) hue-rotate(18deg) brightness(0.92)' }}
        draggable={false}
      />
    </div>

    {/* Labels */}
    <div className="absolute top-5 left-5 bg-white/90 backdrop-blur-sm rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-slate-600 shadow-sm">
      <span className="text-slate-400">Before •</span> Uncalibrated
    </div>
    <div className="absolute top-5 right-5 bg-[#0A2A63] text-white rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest shadow-sm inline-flex items-center gap-1.5">
      <TrendingUp className="h-3 w-3 text-[#FF4E50]" /> After • Calibrated
    </div>

    {/* LAB chips overlay - left side (bad) */}
    <div className="absolute bottom-5 left-5 flex gap-1.5">
      {BAD_CHIPS.map((c) => (
        <div key={c} className="h-8 w-8 rounded-lg shadow-md border-2 border-white/60" style={{ backgroundColor: c }} />
      ))}
    </div>
    {/* LAB chips overlay - right side (good) */}
    <div className="absolute bottom-5 right-5 flex gap-1.5">
      {GOOD_CHIPS.map((c) => (
        <div key={c} className="h-8 w-8 rounded-lg shadow-md border-2 border-white/80" style={{ backgroundColor: c }} />
      ))}
    </div>

    {/* Divider + handle */}
    <div
      className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15),0_0_24px_rgba(0,0,0,0.25)]"
      style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-white shadow-2xl flex items-center justify-center border-4 border-[#0A2A63]">
        <ChevronsLeftRight className="h-5 w-5 text-[#0A2A63]" />
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 rounded-full border-2 border-dashed border-[#FF4E50]/70 animate-spin-slow" />
    </div>
  </div>
);

const BeforeAfterCalibration = () => {
  const [pos, setPos] = useState(52);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  const onMove = (e) => {
    if (!dragging) return;
    setFromClientX(e.clientX);
  };
  const onDown = (e) => {
    setDragging(true);
    setFromClientX(e.clientX);
  };
  const onUp = () => setDragging(false);
  const onTouchStart = (e) => {
    setDragging(true);
    if (e.touches[0]) setFromClientX(e.touches[0].clientX);
  };
  const onTouchMove = (e) => {
    if (e.touches[0]) setFromClientX(e.touches[0].clientX);
  };

  return (
    <section
      className="py-28 relative overflow-hidden"
      data-testid="before-after-calibration-section"
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
    >
      {/* Ambient color background */}
      <div
        className="absolute inset-0 pointer-events-none animate-color-drift"
        style={{
          background:
            'radial-gradient(circle at 20% 20%, rgba(0,183,199,0.10), transparent 45%),' +
            'radial-gradient(circle at 80% 80%, rgba(229,0,126,0.09), transparent 45%),' +
            'radial-gradient(circle at 20% 80%, rgba(255,215,0,0.08), transparent 45%),' +
            'radial-gradient(circle at 80% 20%, rgba(15,123,63,0.08), transparent 45%)',
        }}
      />

      <div className="container-pn relative">
        <CalibrationHeader />

        <CompareSlider
          containerRef={containerRef}
          pos={pos}
          onDown={onDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onUp}
        />

        {/* Bottom color strip signature */}
        <div className="mt-8 max-w-lg">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 font-bold">Reference · ISO 13655</span>
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#0F7B3F] font-bold">Certified</span>
          </div>
          <PrinterStrip />
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterCalibration;
